import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { HIGHARC_DOMAIN } from "common/constants/env";
import type { UserNotificationType } from "common/types/userNotification";
import { assertIsNotNull } from "common/utils/assertOfType";
import { bypassCognito } from "common/utils/context";
import { isHigharcDemoAccountEmail } from "common/utils/higharcDemoAccountEmail";
import { mutate } from "swr";
import * as cognito from "./cognito";
import * as cookie from "./cookie";
import { accountSwitcherPath, adminPath, builderHomePagePath, loginPath } from "./routeHelpers";
import { userIsSuperAdmin } from "./security/shared";
import { putUser, syncUser, syncInternalUserSuperAdminRole } from "./storage/userStorage";

export const VALIDATION_ENDPOINT = `${WEB_API_BASE_PATH}/users/me`;

type AuthResponse = Promise<[string, null] | [null, UserNotificationType]>;

export const login = async (
  email: string,
  password: string,
  redirectTo?: string,
  onError?: (e: any) => void,
): Promise<[string, null] | [null, UserNotificationType | null]> => {
  if (bypassCognito()) {
    return await postLoginSuccessWithoutCognito(email, redirectTo);
  }

  try {
    await cognito.logIn(email, password);
  } catch (e) {
    if (onError) {
      onError(e);
      return [null, null];
    }
    console.error(e);
    return [null, { messageType: "error", message: String(e.message) }];
  }

  return await postLoginSuccess(redirectTo);
};

export const logout = async (userId: string, noRedirect?: boolean): Promise<void> => {
  try {
    // clear any role impersonation settings
    await putUser(userId, { tempUserRole: null });

    if (!bypassCognito()) {
      await cognito.logOut();
    }

    cookie.remove(cognito.HIGHARC_USER_COOKIE);

    // Only reason to make this true is for user invite screen
    if (!noRedirect) {
      window.location.href = "/account/login";
    }
  } catch (e) {
    console.error(e);
  }
};

export const getPostLoginRedirect = async (user: ClientUserHydrated | null, redirectTo?: string): AuthResponse => {
  if (!user) {
    return [loginPath(redirectTo), null];
  }

  // Internal Higharc user in Cognito but not in DB (fresh sandbox or PR environment DB).
  // Skip when they already belong to a builder — e.g. invited as Drafter with adminRole NONE.
  if (
    user.email?.endsWith(`@${HIGHARC_DOMAIN}`) &&
    user.adminRole === "NONE" &&
    user.builderAccounts.length === 0 &&
    !isHigharcDemoAccountEmail(user.email)
  ) {
    await syncInternalUserSuperAdminRole(user.email);

    // update SWR
    await mutate(VALIDATION_ENDPOINT, { ...user, adminRole: "SUPER_ADMIN" });

    if (redirectTo) {
      return [redirectTo, null];
    }
    return [adminPath(), null];
  }

  if (redirectTo) {
    return [redirectTo, null];
  }

  if (userIsSuperAdmin(user)) {
    return [adminPath(), null];
  }

  if (user.builderAccounts.length === 1) {
    return [builderHomePagePath(user.builderAccounts[0].builderId), null];
  } else if (user.builderAccounts.length > 1) {
    return [accountSwitcherPath(), null];
  }

  // Hitting this means something is wrong, likely due to a user that is:
  //   1. Not a member of any builder
  //   2. Not a Super or System Admin
  // Previously, returning "/" would cause infinite redirect loops in local dev.
  //
  // Now, we log the user out with a clear error message. This would likely only
  // ever be seen by an internal Higharc user.
  await logout(user.id, true);
  return [
    null,
    {
      messageType: "error",
      message: "User does not belong to any builder account, contact Higharc support for additional help.",
    },
  ];
};

const postLoginSuccess = async (redirectTo?: string): AuthResponse => {
  const cognitoUser = cognito.getUser();
  assertIsNotNull(cognitoUser);
  const cognitoName = cognitoUser.getUsername();

  const session = await cognito.getSession(cognitoUser);
  const idToken = session.getIdToken();
  const jwtToken = idToken.getJwtToken();

  cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ cognitoName, jwtToken }));

  const { given_name: firstName, family_name: lastName } = idToken.payload;

  // ensure parity between cognito account and database user
  const [user, msg] = await syncUser(cognitoName, firstName, lastName);
  if (msg) {
    return [null, msg];
  }

  // update SWR
  await mutate(VALIDATION_ENDPOINT, user);

  return getPostLoginRedirect(user, redirectTo);
};

async function postLoginSuccessWithoutCognito(email: string, redirectTo?: string): AuthResponse {
  cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ cognitoName: email, jwtToken: email }));

  // Grab user by email
  const [user, msg] = await syncUser(email, "", "");
  if (msg) {
    return [null, msg];
  }

  // update SWR
  await mutate(VALIDATION_ENDPOINT, user);

  return getPostLoginRedirect(user, redirectTo);
}
